/* eslint-disable eqeqeq */
import React, { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { observer } from 'mobx-react-lite';
import { Field, Form } from 'mobx-react-form';
import { isEqual, isNumber, multiply, sum, toNumber } from 'lodash';

import {
  RentRollTableColumn,
  CommercialRentRollUnit,
  CommercialUses,
  CommercialLeaseStatusTypes,
} from 'types/underwriting/income-approach';
import { formatCurrency } from 'components/underwriting/utils/formatters';
import { convertUndefinedToNull } from 'components/underwriting/utils/helpers';
import { renderSingleSelectCell } from 'components/underwriting/utils/cell-renders';
import { divide } from 'shared/utils/numberOperations';
import { formatInt } from 'shared/utils/numberFormatters';
import { Table } from 'components/shared/Table';
import { DISABLE_TABLE_NUMBER_INPUT_CONTROLS_SX } from 'components/underwriting/constants';

const getRentTotals = (units: CommercialRentRollUnit[] = [], isProjectedRentRoll: boolean) => {
  const rentTotalMonthly = units.reduce((total: number, unit: CommercialRentRollUnit) => {
    return sum([total, unit.rent || 0]);
  }, 0);

  const rentTotalAnnual = multiply(rentTotalMonthly, 12);

  const projectedRentTotalMonthly = units.reduce((total: number, unit: CommercialRentRollUnit) => {
    return sum([total, unit.projectedRent || 0]);
  }, 0);

  const projectedRentTotalAnnual = multiply(projectedRentTotalMonthly, 12);

  const totalSquareFootage = units.reduce((total: number, unit: CommercialRentRollUnit) => {
    return sum([total, unit.squareFootage || 0]);
  }, 0);

  const rentTotalPerSF = units.reduce((total: number, unit: CommercialRentRollUnit) => {
    const rentToUse = isProjectedRentRoll ? unit.projectedRent : unit.rent;
    if (!isNumber(rentToUse) || !isNumber(unit.squareFootage) || unit.squareFootage === 0) {
      return total;
    }

    return sum([total, divide(rentToUse, unit.squareFootage)]);
  }, 0);

  return {
    totalSquareFootage,
    rentTotalMonthly,
    rentTotalAnnual,
    rentTotalPerSF,
    projectedRentTotalMonthly,
    projectedRentTotalAnnual,
  };
};

interface RentRollTableProps {
  columns: RentRollTableColumn[];
  form: Form;
  isProjectedRentRoll?: boolean;
}

const RentRollTable = observer(({ columns, form, isProjectedRentRoll = false }: RentRollTableProps) => {
  const rentRollField: Field = form.$('incomeApproach.commercial.rentRoll');
  const rentRoll: CommercialRentRollUnit[] = rentRollField.value;

  const handleProcessRowUpdate = (newRow: any, oldRow: any) => {
    if (newRow.isTotalRow || isEqual(newRow, oldRow)) {
      return oldRow;
    }
    const currentRentRoll = rentRollField.value;
    const targetIndex = currentRentRoll.findIndex((unit: CommercialRentRollUnit) => {
      return unit.id == oldRow.id;
    });

    if (targetIndex > -1) {
      /*
       * This is a workaround to ensure mobx-react-form updates properly numbers
       * If `undefined` is passed to `number` field - update will be ignored, causing mismatch in form and table data.
       */
      const sanitizedRow = convertUndefinedToNull({
        ...newRow,
        projectedRent: isProjectedRentRoll ? newRow.projectedRent : newRow.rent,
      });

      currentRentRoll[targetIndex] = sanitizedRow;
      rentRollField.set(currentRentRoll);

      return sanitizedRow;
    } else {
      return oldRow;
    }
  };

  const gridColumns: GridColDef[] = useMemo(() => {
    return columns.map((column) => {
      const columnName = column.data || '';

      let width = column.data === 'index' ? 20 : 110;
      let align: 'left' | 'right' | 'center' = 'left';
      let editable = !isProjectedRentRoll;
      let type: 'string' | 'number' | 'singleSelect' = 'string';
      let valueOptions: string[] = [];
      let valueFormatter: ((value: any) => string) | undefined;
      let renderCell: ((params: any) => React.ReactNode) | undefined;

      switch (columnName) {
        case 'index':
          editable = false;
          align = 'right';
          renderCell = (params: any) => {
            return params.row.index != null ? params.row.index + 1 : undefined;
          };
          break;
        case 'squareFootage':
          type = 'number';
          align = 'right';
          valueFormatter = (value: any) => {
            if (value == null || value === '') return '';
            return formatInt(value);
          };
          break;
        case 'rent':
          width = isProjectedRentRoll ? 130 : 120;
          type = 'number';
          align = 'right';
          valueFormatter = (value: any) => {
            if (value == null || value === '') return '';
            return formatCurrency(value);
          };
          break;
        case 'projectedRent':
          width = isProjectedRentRoll ? 130 : 120;
          type = 'number';
          align = 'right';
          editable = true;
          valueFormatter = (value: any) => {
            if (value == null || value === '') return '';
            return formatCurrency(value);
          };
          break;
        case 'number':
        case 'tenant':
          break;
        case 'use':
          width = isProjectedRentRoll ? 120 : 150;
          type = 'singleSelect';
          valueOptions = Object.values(CommercialUses);
          renderCell = renderSingleSelectCell;
          break;
        case 'leaseStatus':
          width = isProjectedRentRoll ? 120 : 150;
          type = 'singleSelect';
          valueOptions = Object.values(CommercialLeaseStatusTypes);
          renderCell = renderSingleSelectCell;
          break;
        case 'annualRent':
          width = isProjectedRentRoll ? 170 : 120;
          type = 'number';
          align = 'right';
          editable = false;
          renderCell = (params: any) => {
            let annualRent = toNumber(params.value || 0);

            if (!params.row.isTotalRow) {
              const rent = toNumber(isProjectedRentRoll ? params.row.projectedRent || 0 : params.row.rent || 0);
              annualRent = multiply(rent, 12);
            }

            return formatCurrency(annualRent);
          };
          break;
        case 'rentPerSF':
          width = isProjectedRentRoll ? 150 : 120;
          type = 'number';
          align = 'right';
          editable = false;
          renderCell = (params: any) => {
            let rentPerSF = toNumber(params.value || 0);

            if (!params.row.isTotalRow) {
              const rent = toNumber(isProjectedRentRoll ? params.row.projectedRent || 0 : params.row.rent || 0);
              const squareFootage = toNumber(params.row.squareFootage || 0);
              rentPerSF = divide(rent, squareFootage);
            }

            return formatCurrency(rentPerSF);
          };
          break;
        default:
          break;
      }

      return {
        field: columnName,
        headerName: column.value,
        width,
        align,
        editable,
        type,
        valueOptions: valueOptions.length > 0 ? valueOptions : undefined,
        valueFormatter,
        renderCell,
      };
    });
  }, [columns, isProjectedRentRoll]);

  const rowsWithIds = useMemo(() => {
    const {
      totalSquareFootage,
      rentTotalMonthly,
      rentTotalAnnual,
      rentTotalPerSF,
      projectedRentTotalMonthly,
      projectedRentTotalAnnual,
    } = getRentTotals(rentRoll, isProjectedRentRoll);

    const dataRows = (rentRoll || []).map((unit, index) => ({ ...unit, index }));

    const totalRow = {
      id: 'total',
      number: 'Total',
      squareFootage: totalSquareFootage,
      rent: rentTotalMonthly,
      projectedRent: projectedRentTotalMonthly,
      annualRent: isProjectedRentRoll ? projectedRentTotalAnnual : rentTotalAnnual,
      rentPerSF: rentTotalPerSF,
      isTotalRow: true,
    };

    return [...dataRows, totalRow];
  }, [rentRoll, isProjectedRentRoll]);

  return (
    <div>
      <Table
        rows={rowsWithIds}
        columns={gridColumns}
        processRowUpdate={handleProcessRowUpdate}
        onProcessRowUpdateError={(error) => {
          console.log({ error });
        }}
        getCellClassName={(params) => {
          if (
            ['rentPerSF', 'annualRent'].includes(params.field) ||
            (isProjectedRentRoll && params.field !== 'projectedRent')
          ) {
            return 'disabled-cell';
          }
          return '';
        }}
        sx={{
          width: 'fit-content',
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #e0e0e0',
            borderRight: '1px solid #f0f0f0',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f5f5f5',
            borderBottom: '2px solid #e0e0e0',
          },

          '& .MuiDataGrid-row[data-id="total"]': {
            backgroundColor: '#f8f8f8',
            '& .MuiDataGrid-cell': {
              backgroundColor: '#f8f8f8',
              color: '#666',
              fontWeight: 'bold',
            },
          },
          '& .disabled-cell': {
            backgroundColor: '#f8f8f8',
            color: '#666',
          },
          ...DISABLE_TABLE_NUMBER_INPUT_CONTROLS_SX,
        }}
        isCellEditable={(params) => {
          if (params.row.isTotalRow) {
            return false;
          }

          if (['rentPerSF', 'annualRent'].includes(params.field)) {
            return false;
          }

          return true;
        }}
      />
    </div>
  );
});

export default RentRollTable;
